import React, { useState } from "react";
import { Alert, Button, Form, Input, Modal, Typography } from "antd";
import { LockOutlined, SafetyOutlined, UserOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  EmailAuthProvider,
  getMultiFactorResolver,
  reauthenticateWithCredential,
  TotpMultiFactorGenerator
} from "firebase/auth";
import { auth } from "configs/FirebaseConfig";

const { Text } = Typography;

const ReauthenticationModal = ({ visible, onSuccess, onCancel, operationName }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [requiresTotpCode, setRequiresTotpCode] = useState(false);
  const [mfaResolver, setMfaResolver] = useState(null);

  const handleReauthenticate = async (values) => {
    setLoading(true);
    setError(null);

    try {
      const user = auth.currentUser;

      if (!user || !user.email) {
        throw new Error("No authenticated user found");
      }

      if (!requiresTotpCode) {
        // First step: try password authentication
        const credential = EmailAuthProvider.credential(user.email, values.password);
        await reauthenticateWithCredential(user, credential);

        // If we get here, no MFA is required
        form.resetFields();
        onSuccess();
      } else {
        // Second step: handle TOTP verification
        if (!mfaResolver) {
          throw new Error("Multi-factor resolver not available");
        }

        // Find the TOTP factor from the resolver hints
        const totpHint = mfaResolver.hints.find((hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID);

        if (!totpHint) {
          throw new Error("TOTP factor not found in resolver hints");
        }

        // Create TOTP assertion with the hint
        const totpAssertion = TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, values.totpCode);

        // Complete the multi-factor reauthentication
        await mfaResolver.resolveSignIn(totpAssertion);

        form.resetFields();
        setRequiresTotpCode(false);
        setMfaResolver(null);
        onSuccess();
      }
    } catch (error) {
      console.error("Reauthentication error:", error);

      if (error.code === "auth/multi-factor-auth-required") {
        // Multi-factor authentication is required - create resolver from error
        const resolver = getMultiFactorResolver(auth, error);
        console.log("MFA required, setting resolver:", resolver);
        setMfaResolver(resolver);
        setRequiresTotpCode(true);
        // Don't show error, just switch to TOTP input
        return;
      }

      // Handle other specific Firebase auth errors
      let errorMessage = t("notifications.reauthentication_failed");

      if (error.code === "auth/wrong-password") {
        errorMessage = t("notifications.incorrect_password");
      } else if (error.code === "auth/invalid-verification-code") {
        errorMessage = t("notifications.invalid_totp_code");
      } else if (error.code === "auth/too-many-requests") {
        errorMessage = t("notifications.too_many_attempts");
      } else if (error.code === "auth/network-request-failed") {
        errorMessage = t("notifications.network_error");
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setError(null);
    setRequiresTotpCode(false);
    setMfaResolver(null);
    onCancel();
  };

  return (
    <Modal
      title={t("reauthentication_required")}
      visible={visible}
      onCancel={handleCancel}
      footer={null}
      destroyOnClose
      width={400}
    >
      <div style={{ marginBottom: "16px" }}>
        <Text type="secondary">
          {requiresTotpCode
            ? t("enter_totp_code_to_continue")
            : t("reauthentication_description", { operation: operationName })}
        </Text>
      </div>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: "16px" }} />}

      <Form form={form} layout="vertical" onFinish={handleReauthenticate} autoComplete="off">
        <Form.Item name="email" label={t("email")} initialValue={auth.currentUser?.email}>
          <Input prefix={<UserOutlined />} disabled value={auth.currentUser?.email} />
        </Form.Item>

        <Form.Item
          name="password"
          label={t("password")}
          rules={[
            {
              required: true,
              message: t("form.password_required"),
            },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder={t("form.enter_password")}
            autoFocus={!requiresTotpCode}
          />
        </Form.Item>

        {requiresTotpCode && (
          <Form.Item
            name="totpCode"
            label={t("totp_code")}
            rules={[
              {
                required: true,
                message: t("form.totp_code_required"),
              },
              {
                len: 6,
                message: t("form.totp_code_length"),
              },
            ]}
          >
            <Input
              prefix={<SafetyOutlined />}
              placeholder={t("enter_6_digit_code")}
              maxLength={6}
              autoFocus={requiresTotpCode}
            />
          </Form.Item>
        )}

        <Form.Item style={{ marginBottom: 0, marginTop: "24px" }}>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
            <Button onClick={handleCancel}>{t("cancel")}</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {t("confirm")}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ReauthenticationModal;
